export const TARGET_ESCROW_FLOWS = [
    {
        title: 'Backend Tasks',
        description:
            'Company and employee records now come from the CronPay backend instead of IPFS metadata.',
        icon: 'fa-solid fa-database',
    },
    {
        title: 'Fund Escrow',
        description:
            'The frontend still sends the wallet transaction to the contract when a task needs funding or payout.',
        icon: 'fa-solid fa-wallet',
    },
    {
        title: 'Sync Status',
        description:
            'Task status, refund flow, and notifications are managed in MySQL while contract references stay attached to each task.',
        icon: 'fa-solid fa-arrows-rotate',
    },
];

/** Read `released` from escrows() whether viem returns a struct or tuple. */
export const readOnChainReleased = (onChain) => {
    if (!onChain) return false;
    if (typeof onChain.released === 'boolean') return onChain.released;
    if (Array.isArray(onChain) && onChain.length > 8) return Boolean(onChain[8]);
    return false;
};

export const JOB_WORK_STATUS = {
    NONE: 0,
    IN_PROGRESS: 1,
    SUBMITTED: 2,
    REWORK_REQUESTED: 3,
    RELEASED: 4,
    DISPUTED: 5,
    CANCELLED: 6,
    REFUND_REQUESTED: 7,
    REFUNDED: 8,
};

/** Normalize getJobWorkState whether viem returns a struct or plain tuple. */
export const normalizeJobWorkState = (raw) => {
    if (!raw) return null;
    return {
        workStatus: Number(raw.workStatus ?? raw[0] ?? 0),
        submittedAt: Number(raw.submittedAt ?? raw[1] ?? 0),
        reviewStartedAt: Number(raw.reviewStartedAt ?? raw[2] ?? 0),
        reworkRequestedAt: Number(raw.reworkRequestedAt ?? raw[3] ?? 0),
        reworkCount: Number(raw.reworkCount ?? raw[4] ?? 0),
        reviewOnHold: Boolean(raw.reviewOnHold ?? raw[5] ?? false),
        claimableAt: Number(raw.claimableAt ?? raw[6] ?? 0),
        submissionURI: raw.submissionURI ?? raw[7] ?? '',
        submissionHash: raw.submissionHash ?? raw[8] ?? '',
        reworkURI: raw.reworkURI ?? raw[9] ?? '',
        reworkHash: raw.reworkHash ?? raw[10] ?? '',
    };
};

/** Normalize escrows() whether viem returns a struct or plain tuple. */
export const normalizeJobEscrowState = (raw) => {
    if (!raw) return null;
    if (!Array.isArray(raw) && typeof raw === 'object') {
        return {
            ...raw,
            createdAt: Number(raw.createdAt ?? 0),
            dueDate: Number(raw.dueDate ?? 0),
            accepted: Boolean(raw.accepted),
            released: Boolean(raw.released),
            disputed: Boolean(raw.disputed),
            cancelled: Boolean(raw.cancelled),
            refundRequested: Boolean(raw.refundRequested),
        };
    }
    // Getter facet order: company, employee, amount, metadataURI, metadataHash,
    // createdAt, dueDate, accepted, released, disputed, refundRequested, cancelled
    return {
        company: raw[0],
        employee: raw[1],
        amount: raw[2],
        metadataURI: raw[3] ?? '',
        metadataHash: raw[4],
        createdAt: Number(raw[5] ?? 0),
        dueDate: Number(raw[6] ?? 0),
        accepted: Boolean(raw[7]),
        released: Boolean(raw[8]),
        disputed: Boolean(raw[9]),
        refundRequested: Boolean(raw[10]),
        cancelled: Boolean(raw[11]),
    };
};

export const getJobWorkStatus = (task) => {
    const work = normalizeJobWorkState(task?.onChainWork) || task?.onChainWork;
    const raw = work?.workStatus;
    if (raw === undefined || raw === null) return JOB_WORK_STATUS.NONE;
    return Number(raw);
};

export const resolveTaskStatus = (task) => {
    const dbStatus = String(task?.status || '').toLowerCase();
    const paymentStatus = String(task?.payment?.status || '').toLowerCase();
    const onChain = task?.onChain;
    const hasRefundTx = Boolean(String(task?.payment?.refundTxHash || '').trim());

    if (
        dbStatus === 'refunded' ||
        paymentStatus === 'refunded' ||
        onChain?.cancelled ||
        hasRefundTx
    ) {
        return 'refunded';
    }

    const onChainReleased = readOnChainReleased(onChain);
    // approveRefund() marks released=true while refundRequested stays true
    if (onChainReleased && onChain?.refundRequested) {
        return 'refunded';
    }

    if (onChainReleased) return 'released';
    if (onChain?.disputed) return 'disputed';

    if (
        onChain?.refundRequested ||
        dbStatus === 'refund_requested' ||
        paymentStatus === 'refund_requested'
    ) {
        return 'refund_requested';
    }

    if (paymentStatus === 'released') return 'released';

    const workStatus = getJobWorkStatus(task);
    if (workStatus === JOB_WORK_STATUS.REWORK_REQUESTED) return 'rejected';
    if (workStatus === JOB_WORK_STATUS.SUBMITTED) return 'submitted';

    return task?.status || 'pending';
};

/** Employee may submit only after accepting the task (and on-chain escrow when funded). */
export const canEmployeeSubmitWork = (task, normalizedWallet) => {
    if (!task || !normalizedWallet) return false;

    const employeeWallet = String(task?.employee?.wallet || '')
        .trim()
        .toLowerCase();
    if (employeeWallet !== String(normalizedWallet || '').trim().toLowerCase()) {
        return false;
    }

    const status = resolveTaskStatus(task);
    const submissions = Array.isArray(task.submissions) ? task.submissions : [];
    if (
        status === 'submitted' &&
        submissions.length === 0 &&
        getJobWorkStatus(task) === JOB_WORK_STATUS.SUBMITTED
    ) {
        return true;
    }
    if (!['active', 'rejected'].includes(status)) {
        return false;
    }

    if (task.escrowId) {
        const chainAccepted = isEscrowAcceptedOnChain(task);
        if (chainAccepted === false) {
            return false;
        }
    }

    return true;
};

export const getJobReviewClaimableAt = (task) => {
    const work = normalizeJobWorkState(task?.onChainWork) || task?.onChainWork;
    const raw = work?.claimableAt;
    const value = Number(raw || 0);
    return value > 0 ? value : null;
};

export const canEmployeeClaimJobPayment = (
    task,
    normalizedWallet,
    nowSec = Math.floor(Date.now() / 1000),
) => {
    if (!task?.escrowId || !normalizedWallet) return false;
    const employeeWallet = String(task?.employee?.wallet || '')
        .trim()
        .toLowerCase();
    if (employeeWallet !== String(normalizedWallet || '').trim().toLowerCase()) {
        return false;
    }
    if (resolveTaskStatus(task) !== 'submitted') return false;
    if (readOnChainReleased(task?.onChain)) return false;
    const work = normalizeJobWorkState(task?.onChainWork) || task?.onChainWork;
    if (work?.reviewOnHold) return false;
    if (getJobWorkStatus(task) !== JOB_WORK_STATUS.SUBMITTED) return false;
    const claimableAt = getJobReviewClaimableAt(task);
    return claimableAt !== null && nowSec >= claimableAt;
};

/** Default accept window before company can reclaim unaccepted escrow (48h). Overridden by on-chain `acceptReclaimDelay`. */
export const DEFAULT_ESCROW_RECLAIM_DELAY_SECONDS = Number(
    process.env.REACT_APP_ESCROW_RECLAIM_DELAY_SECONDS || 48 * 3600,
);

export const readOnChainCreatedAt = (onChain) => {
    if (!onChain) return 0;
    if (onChain.createdAt !== undefined && onChain.createdAt !== null) {
        return Number(onChain.createdAt);
    }
    if (Array.isArray(onChain) && onChain.length > 5) {
        return Number(onChain[5]);
    }
    return 0;
};

export const getEscrowReclaimAvailableAt = (task, reclaimDelaySeconds) => {
    const createdAt = readOnChainCreatedAt(task?.onChain);
    if (!createdAt) return null;
    const delay =
        Number(reclaimDelaySeconds) > 0
            ? Number(reclaimDelaySeconds)
            : DEFAULT_ESCROW_RECLAIM_DELAY_SECONDS;
    return createdAt + delay;
};

/** Company may reclaim locked funds after accept window if employee never accepted. */
export const canCompanyReclaimUnacceptedEscrow = (
    task,
    reclaimDelaySeconds,
    nowSec = Math.floor(Date.now() / 1000),
) => {
    if (!task?.escrowId) return false;
    if (isEscrowAcceptedOnChain(task) === true) return false;
    if (readOnChainReleased(task?.onChain) || task?.onChain?.cancelled) {
        return false;
    }
    const availableAt = getEscrowReclaimAvailableAt(task, reclaimDelaySeconds);
    return availableAt !== null && nowSec >= availableAt;
};

export const formatReclaimCountdown = (task, reclaimDelaySeconds) => {
    const availableAt = getEscrowReclaimAvailableAt(task, reclaimDelaySeconds);
    if (availableAt === null) return null;
    const remaining = availableAt - Math.floor(Date.now() / 1000);
    if (remaining <= 0) return null;
    const hours = Math.floor(remaining / 3600);
    const mins = Math.floor((remaining % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
};

export const formatReclaimDelayLabel = (reclaimDelaySeconds) => {
    const seconds =
        Number(reclaimDelaySeconds) > 0
            ? Number(reclaimDelaySeconds)
            : DEFAULT_ESCROW_RECLAIM_DELAY_SECONDS;
    if (seconds % 86400 === 0) return `${seconds / 86400} day(s)`;
    if (seconds % 3600 === 0) return `${seconds / 3600} hour(s)`;
    return `${Math.round(seconds / 60)} minute(s)`;
};

/** Completed work or payment collected — shown on History, hidden from active task lists. */
/** On-chain transaction hashes linked to a task (deduped). */
export const getTaskTransactionHashes = (task) => {
    if (!task) return [];

    const seen = new Set();
    const entries = [];

    const add = (label, hash) => {
        const normalized = String(hash || '').trim();
        if (!normalized) return;
        const key = normalized.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        entries.push({ label, hash: normalized });
    };

    add('Escrow funded', task.txHash);
    add('Escrow funded', task.payment?.txHash);
    add('Payment released', task.payment?.releaseTxHash);
    add('Refund', task.payment?.refundTxHash);

    return entries;
};

export const isTaskInHistory = (task) => {
    const status = resolveTaskStatus(task);
    const paymentStatus = String(task?.payment?.status || '').toLowerCase();
    return (
        status === 'released' ||
        status === 'approved' ||
        paymentStatus === 'released'
    );
};

/** Proxy `escrows(id)` struct: `accepted` may be a named field or tuple index 7. */
export const isEscrowAcceptedOnChain = (task) => {
    const o = task?.onChain;
    if (!o) return undefined;
    if (typeof o.accepted === 'boolean') return o.accepted;
    if (Array.isArray(o) && o.length > 7) return Boolean(o[7]);
    return undefined;
};

/**
 * Dispute is only available after the freelancer has accepted the assignment.
 * Matches intended product flow (and upcoming on-chain `accepted` guard).
 */
export const canRaiseJobDispute = (task) => {
    if (!task?.escrowId || task?.onChain?.cancelled) return false;
    const status = resolveTaskStatus(task);
    if (
        ['pending', 'approved', 'released', 'disputed', 'refund_requested', 'refunded'].includes(
            status,
        )
    ) {
        return false;
    }
    return isEscrowAcceptedOnChain(task) === true;
};

export const formatTaskStatus = (status) =>
    String(status || 'pending')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase());

/** Status chips for task list filtering (matches `resolveTaskStatus`). */
export const TASK_STATUS_FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'pending', label: 'Pending' },
    { id: 'active', label: 'Active' },
    { id: 'submitted', label: 'Submitted' },
];

export const taskMatchesStatusFilter = (task, filterId) => {
    if (!filterId || filterId === 'all') return true;
    const s = resolveTaskStatus(task);
    switch (filterId) {
        case 'pending':
            return s === 'pending';
        case 'active':
            return s === 'active';
        case 'submitted':
            return s === 'submitted';
        case 'approved':
            return s === 'approved';
        case 'finished':
            return s === 'released';
        case 'disputed':
            return s === 'disputed';
        case 'refund':
            return s === 'refund_requested' || s === 'refunded';
        default:
            return true;
    }
};
